/** Simple SVG / CSS charts — no external chart library. */

export function MiniBarChart({
  title,
  series,
  color = 'var(--green)',
  format,
}: {
  title: string;
  series: Array<{ label: string; value: number }>;
  color?: string;
  format?: (v: number) => string;
}) {
  const max = Math.max(1, ...series.map((s) => s.value || 0));
  return (
    <div className="card">
      <div className="card-title">{title}</div>
      {series.length === 0 ? (
        <p className="muted">No data</p>
      ) : (
        <div className="mini-bars" role="img" aria-label={title}>
          {series.map((s) => (
            <div key={s.label} className="mini-bar-col" title={`${s.label}: ${format ? format(s.value) : s.value}`}>
              <div className="mini-bar-track">
                <div
                  className="mini-bar-fill"
                  style={{ height: `${Math.max(4, Math.round(((s.value || 0) / max) * 100))}%`, background: color }}
                />
              </div>
              <div className="mini-bar-label">{s.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DonutChart({
  title,
  segments,
  bare = false,
}: {
  title: string;
  segments: Array<{ label: string; value: number; color: string }>;
  bare?: boolean;
}) {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0) || 1;
  const size = 140;
  const stroke = 18;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  const inner = (
    <div className="donut-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={title}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e8f0eb" strokeWidth={stroke} />
        {segments.map((s) => {
          const len = ((s.value || 0) / total) * c;
          const el = (
            <circle
              key={s.label}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={stroke}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          );
          offset += len;
          return el;
        })}
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" className="donut-total">
          {total}
        </text>
      </svg>
      <ul className="donut-legend">
        {segments.map((s) => (
          <li key={s.label}>
            <span className="dot" style={{ background: s.color }} />
            {s.label}
            <strong>{s.value}</strong>
          </li>
        ))}
      </ul>
    </div>
  );

  if (bare) return inner;
  return (
    <div className="card">
      <div className="card-title">{title}</div>
      {inner}
    </div>
  );
}

export function LineChart({
  title,
  points,
  color = 'var(--green)',
  format,
  bare = false,
  currentPointIndex,
}: {
  title: string;
  points: Array<{ label: string; value: number }>;
  color?: string;
  format?: (v: number) => string;
  bare?: boolean;
  currentPointIndex?: number;
}) {
  const w = 480;
  const h = 180;
  const pad = 28;
  const max = Math.max(1, ...points.map((p) => p.value || 0));
  const step = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0;
  const coords = points.map((p, i) => ({
    x: pad + i * step,
    y: h - pad - ((p.value || 0) / max) * (h - pad * 2),
  }));
  const path = coords.length > 2
    ? coords.slice(1).reduce((d, pt, i) => {
        const previous = coords[i];
        const midpointX = (previous.x + pt.x) / 2;
        return `${d} Q${previous.x},${previous.y} ${midpointX},${(previous.y + pt.y) / 2}`;
      }, `M${coords[0].x},${coords[0].y}`) + ` T${coords[coords.length - 1].x},${coords[coords.length - 1].y}`
    : coords.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x},${pt.y}`).join(' ');
  const area = coords.length
    ? `${path} L${coords[coords.length - 1].x},${h - pad} L${coords[0].x},${h - pad} Z`
    : '';

  const svg = (
    <svg viewBox={`0 0 ${w} ${h}`} className="line-chart" role="img" aria-label={title}>
      {[0, 1, 2, 3].map((line) => {
        const y = pad + (line * (h - pad * 2)) / 3;
        return <line key={line} x1={pad} y1={y} x2={w - pad} y2={y} className="chart-grid-line" />;
      })}
      <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#d7e6dd" />
      <line x1={pad} y1={pad} x2={pad} y2={h - pad} stroke="#d7e6dd" />
      {typeof currentPointIndex === 'number' && coords[currentPointIndex] ? (
        <line x1={coords[currentPointIndex].x} y1={pad} x2={coords[currentPointIndex].x} y2={h - pad} className="chart-current-line" />
      ) : null}
      {area ? <path d={area} fill={color} opacity={0.12} /> : null}
      <path d={path} fill="none" stroke={color} strokeWidth={2.5} />
      {coords.map((pt, i) => (
          <circle key={i} cx={pt.x} cy={pt.y} r={i === currentPointIndex ? 5 : 3} fill={color} className={i === currentPointIndex ? 'chart-current-point' : undefined}>
          <title>{`${points[i].label}: ${format ? format(points[i].value) : points[i].value}`}</title>
        </circle>
      ))}
      {points.map((p, i) =>
        i % Math.ceil(points.length / 8 || 1) === 0 ? (
          <text key={p.label} x={coords[i].x} y={h - 8} textAnchor="middle" className="chart-axis">
            {p.label}
          </text>
        ) : null,
      )}
      <text x={4} y={pad} className="chart-axis">
        {format ? format(max) : max}
      </text>
    </svg>
  );

  if (bare) return points.length === 0 ? <p className="muted">No data</p> : svg;
  return (
    <div className="card">
      <div className="card-title">{title}</div>
      {points.length === 0 ? <p className="muted">No data</p> : svg}
    </div>
  );
}

export function StatCard({
  label,
  value,
  sub,
  icon,
}: {
  label: string;
  value: unknown;
  sub?: string;
  icon?: string;
}) {
  return (
    <div className="kpi-card dash-stat">
      {icon ? <div className="dash-stat-icon">{icon}</div> : null}
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value == null || value === '' ? '—' : String(value)}</div>
      {sub ? <div className="dash-stat-sub">{sub}</div> : null}
    </div>
  );
}
